import { useEffect, useState } from 'react';
// @mui
import {
  Box,
  Card,
  Container,
  Divider,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
// components
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import { TableHeadCustom, TableNoData, TableSkeleton } from 'src/components/table';
import ColorInfo from 'src/components/table/ColorInfo';
// services
import AxiosAll from 'src/services/axiosAll';
// hooks
import useSettings from 'src/hooks/useSettings';
import useTable from 'src/hooks/useTable';
// sections
import CompTaskRow from 'src/sections/@dashboard/task/list/CompTaskRow';
import TaskFilterToolbar from './TaskFilterToolbar';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'title', label: 'Title', align: 'left' },
  { id: 'ticket', label: 'Ticket', align: 'left' },
  { id: 'project', label: 'Project', align: 'left' },
  { id: 'dueDate', label: 'Due Date', align: 'left' },
  { id: 'priority', label: 'Priority', align: 'center' },
  { id: 'status', label: 'Status', align: 'left' },
  { id: 'assignedTo', label: 'Assigned To', align: 'left' },
  { id: 'created_by', label: 'Created By', align: 'left' },
  { id: 'action', label: 'Action', align: 'left' },
];

// ----------------------------------------------------------------------

export default function TaskFilterPage() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    //
    selected,
    onSelectRow,
    onSelectAllRows,
    //
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const { themeStretch } = useSettings();

  // Redux state
  const { userDetails, userList, projectList, priorityList } = useSelector((state) => state.loginReducer);
  const { token } = userDetails;

  // Local state
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  // Filter states
  const [query, setQuery] = useState('');
  const [statusId, setStatusId] = useState(4);
  const [userId, setUserId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [status, setStatus] = useState([]);

  const getStatus = async () => {
    setStatus([]);
    const response = await AxiosAll('get', `/task-statuses`, {}, token);
    if (response?.status === 200) {
      setStatus(response?.data?.data?.data);
    }
  };

  useEffect(() => {
    getStatus();
  }, []);

  const getTasks = async () => {
    setLoading(true);
    try {
      // Construct query parameters based on filter states
      const queryParams = new URLSearchParams();
      if (query) queryParams.append('query', query);
      if (statusId) queryParams.append('status', statusId);
      if (userId) queryParams.append('user_id', userId);
      if (projectId) queryParams.append('project_id', projectId);
      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);
      if (sortBy) queryParams.append('sort_by', sortBy);
      if (sortDirection) queryParams.append('sort_direction', sortDirection);

      // Add pagination params
      queryParams.append('page', page + 1);
      queryParams.append('limit', rowsPerPage);

      const response = await AxiosAll('get', `/tasks/filters?${queryParams.toString()}`, {}, token);

      if (response?.data?.statusCode === 200) {
        setTableData(response.data.data.data);
        setTotal(response.data.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      enqueueSnackbar('Failed to load tasks', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Reset filters
  const handleResetFilters = () => {
    setQuery('');
    setStatusId('');
    setUserId('');
    setProjectId('');
    setStartDate('');
    setEndDate('');
    setSortBy('created_at');
    setSortDirection('DESC');
    setPage(0);
  };

  // Handle query search
  const handleFilterName = (value) => {
    setQuery(value);
    setPage(0);
  };

  useEffect(() => {
    getTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, query, statusId, userId, projectId, startDate, endDate, sortBy, sortDirection]);

  const isNotFound = !loading && !tableData.length;

  return (
    <Page title="Comprehensive Task Listr">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Comprehensive Task List"
          links={[{ name: 'Dashboard', href: '/dashboard' }, { name: 'Comprehensive Task List' }]}
        />

        <Card>
          <TaskFilterToolbar
            filterQuery={query}
            onFilterName={handleFilterName}
            onResetFilters={handleResetFilters}
            statusId={statusId}
            setStatusId={setStatusId}
            userId={userId}
            setUserId={setUserId}
            projectId={projectId}
            setProjectId={setProjectId}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            projectOptions={projectList || []}
            userOptions={userList || []}
            statusOptions={status || []}
          />

          <Divider />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={tableData.length}
                  numSelected={selected.length}
                  onSort={onSort}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      tableData.map((row) => row.id)
                    )
                  }
                  hideCheckbox
                />

                {loading ? (
                  <>
                    <TableSkeleton />
                    <TableSkeleton />
                    <TableSkeleton />
                    <TableSkeleton />
                    <TableSkeleton />
                  </>
                ) : (
                  <TableBody>
                    {tableData.map((row) => (
                      <CompTaskRow
                        key={row.id}
                        row={row}
                        selected={selected.includes(row.id)}
                        onSelectRow={() => onSelectRow(row.id)}
                        getTasks={getTasks}
                        hideCheckbox
                        showTicket
                        dontShowTime
                      />
                    ))}

                    <TableNoData isNotFound={isNotFound} />
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[20]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />

            <ColorInfo />

            <FormControlLabel
              control={<Switch checked={dense} onChange={onChangeDense} />}
              label="Dense"
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
}
